import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import {
  Anchor,
  BodyText,
  Input,
  Tooltip,
} from '@chargify/custom-ui-components';
import { Icon } from '@chargify/icons';
import { useDeviceInfo } from '@chargify/device-provider';

import { ComponentPreview } from '../../types';
import { FlexRow, Memo } from './ComponentsPreviewRow.styles';

export type Props = {
  /**
   * Components preview row configuration
   */
  row: ComponentPreview;
};

const ComponentsPreviewRow = ({ row }: Props) => {
  const {
    name,
    description,
    disableMemo = false,
    memo = '',
    renderPrice,
    renderControl,
    onMemoChange,
  } = row;
  const { device } = useDeviceInfo();
  const [showMemo, setShowMemo] = useState(!!memo);
  const [inputMemo, setInputMemo] = useState(memo);

  const handleMemoChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputMemo(e?.target?.value);
    onMemoChange(e);
  };

  const isOnMobileDevice = ['tablet', 'mobile'].includes(device);
  const { t } = useTranslation(['subscriptions-manager']);

  return (
    <>
      <div>
        <FlexRow
          alignItems={'baseline'}
          gridColumnGap={7}
          gridRowGap={3}
          display={'grid'}
          gridTemplateColumns={isOnMobileDevice ? '1fr' : '3fr 1fr 1fr'}
        >
          <FlexRow
            alignItems={'center'}
            gridColumnGap={2}
            flex={isOnMobileDevice ? '100%' : '4'}
            justifyContent={isOnMobileDevice ? 'center' : 'flex-start'}
          >
            <BodyText variant="body2" color="gray.dark">
              {name}
            </BodyText>
            {description && (
              <Tooltip
                renderContent={() => <Tooltip.Text message={description} />}
                placement="top-start"
                followCursor
              >
                <Icon type={'questionCircle'} fill={'gray.dark'} size={16} />
              </Tooltip>
            )}
          </FlexRow>
          <FlexRow
            flex={isOnMobileDevice ? '100%' : '1 0 auto'}
            justifyContent={isOnMobileDevice ? 'center' : 'flex-start'}
            justifyItems={'center'}
          >
            {renderPrice()}
          </FlexRow>
          <FlexRow
            flexDirection={isOnMobileDevice ? 'row-reverse' : 'column'}
            alignItems={isOnMobileDevice ? 'center' : 'flex-end'}
            justifyContent={isOnMobileDevice ? 'space-between' : 'normal'}
            flex={isOnMobileDevice ? '100%' : '1 0 auto'}
            gridRowGap={3}
          >
            <div>{renderControl()}</div>
            {!disableMemo &&
              (showMemo ? (
                <Anchor
                  label={t('components_allocations.remove_memo')}
                  iconLeft="close"
                  size="small"
                  onClick={() => {
                    setShowMemo(false);
                    handleMemoChange(null);
                  }}
                />
              ) : (
                <Anchor
                  label={t('components_allocations.add_memo')}
                  iconLeft="edit"
                  size="small"
                  onClick={() => setShowMemo(true)}
                />
              ))}
          </FlexRow>
        </FlexRow>
      </div>
      <AnimatePresence>
        {!disableMemo && showMemo && (
          <Memo
            marginTop={2}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: 'tween' }}
          >
            <Input
              data-testid="input-memo"
              onChange={handleMemoChange}
              value={inputMemo}
              placeholder={t('components_allocations.memo_placeholder')}
              fullWidth
            />
          </Memo>
        )}
      </AnimatePresence>
    </>
  );
};

export default ComponentsPreviewRow;
